import type { components } from '../types/schema';

export const parseMemberCalledMessage = (
  memberCalledMessage: string,
  replacements: Record<string, string> = {}
) => {
  let parsedMessage = memberCalledMessage.replace(/\\n/g, '\n');

  // {} 안의 플레이스홀더를 실제 값으로 교체
  Object.entries(replacements).forEach(([key, value]) => {
    const placeholder = `{${key}}`;
    parsedMessage = parsedMessage.replace(new RegExp(placeholder, 'g'), value);
  });

  return parsedMessage;
};

export const hasFinalConsonant = (word: string): boolean | null => {
  if (!word) return null;
  const lastChar = word[word.length - 1];
  const code = lastChar.charCodeAt(0) - 0xac00;
  if (code < 0 || code > 11171) return null; // 한글이 아니면 null 반환
  return code % 28 !== 0; // 받침 여부를 반환
};

export const getJosa = (
  word: string,
  type: '을/를' | '이/가' | '은/는' | '과/와' | '아/야' | '가/이가'
): string => {
  if (!word) return '';
  if (localStorage.getItem('i18nextLng') !== 'ko') return '';

  const final = hasFinalConsonant(word);
  if (final === null) return '';

  switch (type) {
    case '을/를':
      return final ? '을' : '를';
    case '이/가':
      return final ? '이' : '가';
    case '은/는':
      return final ? '은' : '는';
    case '과/와':
      return final ? '과' : '와';
    case '아/야':
      return final ? '아' : '야';
    case '가/이가':
      return final ? '이가' : '가';
  }
};

export const attachJosa = (
  word: string,
  type: '을/를' | '이/가' | '은/는' | '과/와' | '아/야' | '가/이가'
): string => {
  if (localStorage.getItem('i18nextLng') !== 'ko') return word;

  const josa = getJosa(word, type);
  if (!josa) return word; // 한글이 아니거나 빈 값이면 그냥 반환

  return word + josa;
};

type ChatRoomMessage = components['schemas']['ChatRoomMessage'];

export type MessageGroupFlags = {
  previousDate: ChatRoomMessage['sentAt'] | null | undefined;
  isFirstMessage: boolean;
  isFirstInGroup: boolean;
  isLastInGroup: boolean;
};

const isNotice = (msg: ChatRoomMessage | null) => msg?.messageType === 'notice';
const isTypingNotice = (msg: ChatRoomMessage | null) =>
  isNotice(msg) && msg?.content === '__typing__';
const isSameSender = (a: ChatRoomMessage | null, b: ChatRoomMessage | null) =>
  Boolean(a?.isMine) === Boolean(b?.isMine);

export const getGroupFlags = (messages: ChatRoomMessage[], index: number): MessageGroupFlags => {
  if (index < 0 || index >= messages.length) {
    // index bounds validation
    return {
      previousDate: null,
      isFirstMessage: true,
      isFirstInGroup: true,
      isLastInGroup: true
    };
  }
  const current = messages[index] ?? null;
  const prev = index > 0 ? (messages[index - 1] ?? null) : null;
  const next = index < messages.length - 1 ? (messages[index + 1] ?? null) : null;

  const previousDate = prev?.sentAt;

  // 이전 메시지와 연결되는지 확인
  const connectsWithPrev = !!(
    prev &&
    isSameSender(prev, current) &&
    !isNotice(prev) &&
    !isNotice(current)
  );

  // 다음 메시지와 연결되는지 확인
  let connectsWithNext = !!(
    next &&
    isSameSender(next, current) &&
    !isNotice(next) &&
    !isNotice(current)
  );
  if (!isNotice(current) && isTypingNotice(next) && isSameSender(next, current)) {
    connectsWithNext = true;
  }

  return {
    previousDate,
    isFirstMessage: index === 0,
    isFirstInGroup: !connectsWithPrev, // 이전 메시지와 연결되지 않는 경우 첫 번째 메시지
    isLastInGroup: !connectsWithNext // 다음 메시지와 연결되지 않는 경우 마지막 메시지
  };
};
